import { useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router';

import VideaLogoSm from '@/common-components/elements/icon/svgs/VideaBrandSm';
import { mixpanelEvents } from '@/utils/mixpanel';

import { InvalidLinkMessage } from './components/InvalidLinkMessage';
import { PreQualifyForm } from './components/PreQualifyForm';
import { PreQualifySuccess } from './components/PreQualifySuccess';
import { trackPreQualifyEvent } from './trackPreQualifyEvent';
import type { PreQualifyFormValues } from './types';
import { hasValidQueryParams, parsePreQualifyParams } from './utils';

type PageState = 'form' | 'success';

export const PreQualifyPage = () => {
  const location = useLocation();
  const dealId = new URLSearchParams(location.search).get('deal_id') ?? '';
  const [pageState, setPageState] = useState<PageState>('form');
  const [submittedValues, setSubmittedValues] =
    useState<PreQualifyFormValues | null>(null);

  const initialValues = parsePreQualifyParams(location.search);

  const prevSearchRef = useRef(location.search);
  useEffect(() => {
    if (prevSearchRef.current !== location.search) {
      prevSearchRef.current = location.search;
      setPageState('form');
      setSubmittedValues(null);
    }
  }, [location.search]);

  useEffect(() => {
    if (dealId && hasValidQueryParams(location.search)) {
      trackPreQualifyEvent(dealId, mixpanelEvents.preQualifyFormView, {
        dealId,
      });
    }
  }, [dealId, location.search]);

  const handleSuccess = (values: PreQualifyFormValues) => {
    setSubmittedValues(values);
    setPageState('success');
  };

  const handleStartOver = () => {
    setPageState('form');
  };

  return (
    <div className="vai-flex vai-min-h-screen vai-flex-col vai-items-center vai-bg-gray-50 vai-px-4 vai-py-8">
      <div className="vai-mb-8">
        <VideaLogoSm color="#1a202c" />
      </div>

      <h1 className="vai-mb-6 vai-text-2xl vai-font-semibold">
        Tech Validation
      </h1>

      <div className="vai-w-full vai-max-w-lg vai-rounded-lg vai-bg-white vai-p-6 vai-shadow-sm">
        {!dealId || !hasValidQueryParams(location.search) ? (
          <InvalidLinkMessage />
        ) : pageState === 'form' ? (
          <PreQualifyForm
            key={location.search}
            dealId={dealId}
            initialValues={submittedValues ?? initialValues}
            onSuccess={handleSuccess}
          />
        ) : submittedValues ? (
          <PreQualifySuccess
            dealId={dealId}
            values={submittedValues}
            onStartOver={handleStartOver}
          />
        ) : null}
      </div>
    </div>
  );
};
